import { Sine, TimelineLite, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "../Application";
import { AppProgress } from "../Data/AppProgress";
import { BalloonControlToken } from "../Data/BalloonControlToken";
import * as Constants from "../Data/Constants";
import { PowerupData } from "../Data/PowerupData";
import { Powerups } from "../Data/Powerups";
import { Regions } from "../Data/Regions";
import * as Topics from "../Data/Topics";
import * as VoiceOver from "../Data/VoiceOverKeys";
import { Balloon } from "../GameObjects/Balloon";
import { BalloonParameter } from "../GameObjects/DebugBalloonControls";
import { GameplayLayer } from "../GameObjects/GameplayLayer";
import { Ground } from "../GameObjects/Ground";
import { ObstacleLayer } from "../GameObjects/ObstacleLayer";
import { Obstacle } from "../GameObjects/Obstacles/Obstacle";
import { Onboarding } from "../GameObjects/Onboarding";
import { ParallaxLayer } from "../GameObjects/ParallaxLayer";
import { Powerup } from "../GameObjects/Powerup";
import { PowerupLayer } from "../GameObjects/PowerupLayer";
import { TicketBooth } from "../GameObjects/TicketBooth";
import { InteruptType, VoiceCategory } from "../GameObjects/VoiceOverManager";
import { Physics } from "../Physics/Physics";

export class World extends PIXI.Container {
    private _levelSize: number;
    private _dist: number;
    private _distAsPerc: number;
    private _spawnOffset: number;
    private _speedMod: number;
    private _endProgress: number;
    private _isOnboarding: boolean;
    private _isKeyDown: boolean;

    private _layers: ParallaxLayer[];
    private _background: ParallaxLayer;
    private _sky: PIXI.Sprite;
    private _clouds: ParallaxLayer;
    private _ground: Ground;
    private _powerups: PowerupLayer | undefined;
    private _obstacles: ObstacleLayer | undefined;
    private _balloon: Balloon;
    private _balloonShadow: PIXI.Sprite;
    private _gameplay: GameplayLayer | undefined;
    private _onboarding: Onboarding | undefined;

    private _speedModTween: TweenLite | undefined;
    private _levelEndDelay: TimelineLite | undefined;
    private _subscriptions: any[];
    private _pointerDownCallback: () => void;
    private _pointerUpCallback: () => void;
    private _keyDownCallback: (pEvent: KeyboardEvent) => void;
    private _keyUpCallback: (pEvent: KeyboardEvent) => void;

    constructor() {
        super();
        this._isOnboarding = (AppProgress.onboardingComplete === false);
        this._levelSize = this._isOnboarding ? Constants.ONBOARDING_SIZE : Constants.LEVEL_SIZE;
        this._dist = AppProgress.checkpointReached ? this._levelSize * 0.5 : 0;
        this._distAsPerc = this._dist / this._levelSize;
        this._spawnOffset = 0;
        this._speedMod = 1;
        this._endProgress = 0;
        this._isKeyDown = false;

        this._layers = new Array<ParallaxLayer>();

        this._sky = HLF.PixiUtils.makeSprite("sky");
        this.addChild(this._sky);

        this._clouds = new ParallaxLayer(
            Constants.PARALLAX_SPEED * 0.2,
            Regions.clouds,
            new PIXI.Point(-400, 50),
            new PIXI.Point(250, 700),
            new PIXI.Point(0.6, 1.0),
        );
        this.addChild(this._clouds);
        this._layers.push(this._clouds);

        this._background = new ParallaxLayer(
            Constants.PARALLAX_SPEED * 0.75,
            Regions.activeData.backgrounds,
            new PIXI.Point(-150, 150),
            new PIXI.Point(160, 160),
            new PIXI.Point(0.8, 1.2),
        );
        this.addChild(this._background);
        this._layers.push(this._background);

        this._ground = new Ground(Constants.PARALLAX_SPEED, [ Regions.activeData.terrain ]);
        this.addChild(this._ground);
        this._layers.push(this._ground);

        this._balloonShadow = HLF.PixiUtils.makeSprite("balloon-shadow", Constants.SPRITESHEET_GAMEPLAY);
        this._balloonShadow.position.set(0, 300);
        this.addChild(this._balloonShadow);

        this._balloon = new Balloon(AppProgress.activeBalloon);

        if (this._isOnboarding) {
            this._onboarding = new Onboarding(this._balloon);
            this.addChild(this._onboarding);
            this._layers.push(this._onboarding);
        }
        else {
            this._gameplay = new GameplayLayer();
            this.addChild(this._gameplay);
            this._layers.push(this._gameplay);

            this._powerups = new PowerupLayer(
                Constants.PARALLAX_SPEED,
                Powerups.data,
                new PIXI.Point(650, 1200),
                new PIXI.Point(250, 650),
                new PIXI.Point(1, 1),
            );
            this._powerups.isSpawning = true;
            this.addChild(this._powerups);
            this._layers.push(this._powerups);

            this._obstacles = new ObstacleLayer(
                Constants.PARALLAX_SPEED,
                Regions.activeData.obstacles,
                Regions.activeData.obstacleSpacing,
            );
            this._obstacles.isSpawning = true;
            this.addChild(this._obstacles);
            this._layers.push(this._obstacles);
        }

        this._balloon.position.set(Balloon.DEFAULT_X, -200);
        this.addChild(this._balloon);
        this._balloon.animateIn();

        this._subscriptions = new Array<any>();
        this._subscriptions.push(PubSub.subscribe(
            Topics.ON_DEBUG_BALLOON_PARAM_CHANGED,
            this.onParamChanged.bind(this),
        ));
        this._subscriptions.push(PubSub.subscribe(
            Topics.SPEED_BOOST_START,
            () => { this.setSpeedMod(Balloon.SPEED_BOOST_MULT); },
        ));
        this._subscriptions.push(PubSub.subscribe(
            Topics.SPEED_BOOST_END,
            () => { this.setSpeedMod(1); },
        ));
        this._subscriptions.push(PubSub.subscribe(
            Topics.CHECK_POWERUP_OVERLAP,
            this.preventPowerupOverlap.bind(this),
        ));
        this._subscriptions.push(PubSub.subscribe(
            Topics.STOP_POWERUPS,
            () => { if (this._powerups !== undefined) { this._powerups.isSpawning = false; } },
        ));
        this._subscriptions.push(PubSub.subscribe(
            Topics.RESUME_POWERUPS,
            () => { if (this._powerups !== undefined) { this._powerups.isSpawning = true; } },
        ));

        this._pointerDownCallback = this.onPointerDown.bind(this);
        this._pointerUpCallback = this.onPointerUp.bind(this);
        Application.instance.renderer.plugins.interaction.addListener(
            HLF.Input.Events.POINTER_DOWN,
            this._pointerDownCallback,
        );
        Application.instance.renderer.plugins.interaction.addListener(
            HLF.Input.Events.POINTER_UP,
            this._pointerUpCallback,
        );
        Application.instance.renderer.plugins.interaction.addListener(
            HLF.Input.Events.POINTER_UP_OUTSIDE,
            this._pointerUpCallback,
        );

        this._keyDownCallback = this.onKeyDown.bind(this);
        this._keyUpCallback = this.onKeyUp.bind(this);
        window.addEventListener(HLF.Input.Events.KEY_DOWN, this._keyDownCallback, false);
        window.addEventListener(HLF.Input.Events.KEY_UP, this._keyUpCallback, false);

        let vo: string;
        if (this._isOnboarding === false) {
            if (AppProgress.levelsCompleted === 0) {
                vo = VoiceOver.ONBOARDING_FREEPLAY;
            }
            else {
                const index: number = AppProgress.activeLevel;
                vo = VoiceOver.LEVEL_START_PREFIX + VoiceOver.REGION_SUFFIX[index];
            }
            new TimelineLite().call(() => {
                Application.instance.voiceOver.play(vo, InteruptType.LESS, VoiceCategory.INSTRUCTIONAL);
            }, undefined, this, 0.5);
        }
    }

    public get speedMod(): number {
        return this._speedMod;
    }

    public set speedMod(pValue: number) {
        this._speedMod = pValue;
        for (let i = 0; i < this._layers.length; ++i) {
            this._layers[i].speedMod = pValue;
        }
    }

    public init(pSize: PIXI.Point): void {
        for (let i = 0; i < this._layers.length; ++i) {
            this._layers[i].init(pSize);
        }
        this._spawnOffset = (pSize.x * 0.5 - Balloon.DEFAULT_X) / Constants.LEVEL_SIZE;
        // this._spawnOffset = ((pSize.x - 1024) / this._levelSize) / 2;
    }

    public update(pDeltaTime: number): void {
        for (let i = 0; i < this._layers.length; ++i) {
            // Separate speedMod from deltaTime so it doesnt affect animations
            this._layers[i].update(pDeltaTime);
        }
        this._balloon.update(pDeltaTime);
        const balloonPos: PIXI.Point = this.worldTransform.applyInverse(this._balloon.visualPos);
        this._balloonShadow.position.x = balloonPos.x;
        // Based on y = 150 being the lowest y pos possible
        const shadowDist: number = HLF.MathUtils.clamp(Math.abs(balloonPos.y - 150) / 450, 0, 1);
        this._balloonShadow.alpha = 1 - (0.5 * shadowDist);
        this._balloonShadow.scale.set(1 - (0.5 * shadowDist));

        if (this._isOnboarding) {
            this._onboarding!.checkCollision();
        }
        else {
            this.checkCollision();
        }

        this._dist += Math.abs(Constants.PARALLAX_SPEED) * this._speedMod * pDeltaTime;
        this._distAsPerc = this._dist / this._levelSize;
        PubSub.publishSync(Topics.UPDATE_PROGRESS, this._distAsPerc);

        if (this._endProgress === 0 && (this._distAsPerc + this._spawnOffset) >= 0.95) {
            this._endProgress++;
            if (this._powerups !== undefined) { this._powerups.isSpawning = false; }
            if (this._obstacles !== undefined) { this._obstacles.isSpawning = false; }
            if (this._gameplay !== undefined) { this._gameplay.isSpawning = false; }
        }
        else if (this._endProgress === 1 && (this._distAsPerc) >= 0.95) {
            this._endProgress++;
            this._balloon.active = false;
            this._balloon.prepForEnd();
        }
        else if (this._endProgress === 2 && (this._distAsPerc) >= 0.975) {
            this._endProgress++;
            this._levelEndDelay = new TimelineLite();
            this._levelEndDelay.call(() => {
                this.setSpeedMod(0, 2.0);
                this._balloon.animateOut();
            }, undefined, this, 0.0);
            this._levelEndDelay.call(() => {
                PubSub.publishSync(Topics.LEVEL_COMPLETED, undefined);
            }, undefined, this, 3.0);
        }
    }

    public onResize(pSize: PIXI.Point): void {
        for (let i = 0; i < this._layers.length; ++i) {
            this._layers[i].onResize(pSize);
        }
        this._balloon.onResize(pSize);
    }

    public destroy(pOptions: boolean | PIXI.DestroyOptions = { children: true }): void {
        super.destroy(pOptions);
        if (this._speedModTween !== undefined) { this._speedModTween.kill(); }
        if (this._levelEndDelay !== undefined) { this._levelEndDelay.kill(); }

        for (let i = 0; i < this._subscriptions.length; ++i) {
            PubSub.unsubscribe(this._subscriptions[i]);
        }

        window.removeEventListener(HLF.Input.Events.KEY_DOWN, this._keyDownCallback, false);
        window.removeEventListener(HLF.Input.Events.KEY_UP, this._keyUpCallback, false);
        Application.instance.renderer.plugins.interaction.removeListener(
            HLF.Input.Events.POINTER_DOWN,
            this._pointerDownCallback,
        );
        Application.instance.renderer.plugins.interaction.removeListener(
            HLF.Input.Events.POINTER_UP,
            this._pointerUpCallback,
        );
        Application.instance.renderer.plugins.interaction.removeListener(
            HLF.Input.Events.POINTER_UP_OUTSIDE,
            this._pointerUpCallback,
        );
    }

    private checkCollision(): void {
        if (this._gameplay !== undefined) {
            for (let i = 0; i < this._gameplay.elements.length; ++i) {
                if (Physics.instance.checkCollision(this._balloon, this._gameplay.elements[i])) {
                    this._gameplay.elements[i].onCollision(this._balloon.invulnerable);
                    if (this._gameplay.elements[i] instanceof TicketBooth) {
                        PubSub.publishSync(Topics.TICKET_COLLECTED, undefined);
                        (this._gameplay.elements[i] as TicketBooth).onTicketCollected(
                            this._balloon.getGlobalPosition(),
                        );
                        if (this._isOnboarding === false) {
                            AppProgress.checkpointReached = true;
                            Application.instance.voiceOver.play(
                                HLF.ArrayUtils.random(VoiceOver.TICKET_COLLECT),
                                InteruptType.LESS,
                                VoiceCategory.INSTRUCTIONAL,
                            );
                        }
                    }
                    else {
                        this._balloon.onObstacleHit();
                    }
                }
            }
        }
        if (this._powerups !== undefined) {
            for (let i = 0; i < this._powerups.elements.length; ++i) {
                if (Physics.instance.checkCollision(this._balloon, this._powerups.elements[i])) {
                    this._powerups.elements[i].onCollision(this._balloon.invulnerable);
                    this._balloon.onPowerupCollected(
                        Powerups.data.indexOf(this._powerups.elements[i].data as PowerupData),
                    );
                }
            }
        }
        if (this._obstacles !== undefined) {
            for (let i = 0; i < this._obstacles.elements.length; ++i) {
                if (Physics.instance.checkCollision(this._balloon, this._obstacles.elements[i])) {
                    this._obstacles.elements[i].onCollision(this._balloon.invulnerable);
                    this._balloon.onObstacleHit();
                }
            }
        }
    }

    private onParamChanged(pMessage: string, pToken: BalloonControlToken): void {
        switch (pToken.parameter) {
            case BalloonParameter.HORIZONTAL:
                // this._layers[0].speed = -pToken.value * 0.5;
                for (let i = 0; i < this._layers.length; ++i) {
                    this._layers[i].speed = -pToken.value;
                }
                break;
        }
    }

    private setSpeedMod(pValue: number, pTweenDur: number = 0.5): void {
        if (this._speedModTween !== undefined) { this._speedModTween.kill(); }
        this._speedModTween = TweenLite.to(this, pTweenDur, {
            speedMod: pValue,
            ease: Sine.easeInOut,
        });
    }

    private preventPowerupOverlap(pTopic: string, pPowerup: Powerup): void {
        if (this._gameplay === undefined) {
            return;
        }
        for (let i = 0; i < this._gameplay.elements.length; ++i) {
            if (this._gameplay.elements[i] === pPowerup) {
                continue;
            }
            let attempts: number = 0;
            while (
                attempts < 10 && // Prevent infinite loop
                Physics.instance.checkCollision(pPowerup, this._gameplay.elements[i])
            ) {
                pPowerup.position.y = 384 - HLF.Random.floatBetweenPoint(this._powerups!.yRange);
                attempts++;
            }
        }
    }

    private onPointerDown(): void {
        PubSub.publishSync(Topics.BALLOON_LIFT_START, undefined);
    }

    private onPointerUp(): void {
        PubSub.publishSync(Topics.BALLOON_LIFT_END, undefined);
    }

    private onKeyDown(pEvent: KeyboardEvent): void {
        if (this._isKeyDown === false && HLF.Input.KeyCode.isEventKey(pEvent, HLF.Input.KeyCodes.SPACEBAR)) {
            this._isKeyDown = true;
            PubSub.publishSync(Topics.BALLOON_LIFT_START, undefined);
        }
    }

    private onKeyUp(pEvent: KeyboardEvent): void {
        if (HLF.Input.KeyCode.isEventKey(pEvent, HLF.Input.KeyCodes.SPACEBAR)) {
            this._isKeyDown = false;
            PubSub.publishSync(Topics.BALLOON_LIFT_END, undefined);
        }
    }
}
